import { Accordion, Badge, Group, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { JsonBlock } from '../../JsonBlock';
import type { ComputedRecordLike, RecordTypeI18nPrefix } from './computedRecordTypes';
import { recordDisplayValue } from './computedRecordTypes';
import { formatFactWhen } from './factTypes';
import { FactValueView } from './FactValueView';

export function RecordCard({
    record,
    typeI18nPrefix,
    source,
}: {
    record: ComputedRecordLike;
    typeI18nPrefix: RecordTypeI18nPrefix;
    source?: string | null;
}) {
    const { t } = useTranslation();
    const empty = t('common.empty');
    const title = t(`${typeI18nPrefix}.${record.recordType}`, {
        defaultValue: record.recordType,
    });
    const verified = formatFactWhen(record.lastVerifiedAt);
    const computed = formatFactWhen(record.createdAt);
    const displayValue = recordDisplayValue(record.recordValue ?? {});

    return (
        <Accordion.Item value={record.id}>
            <Accordion.Control>
                <Group gap="sm" wrap="wrap">
                    <Text size="sm" fw={600}>
                        {title}
                    </Text>
                    <Badge variant="light" size="sm">
                        {record.recordType}
                    </Badge>
                    {source ? (
                        <Text size="xs" c="dimmed">
                            {t('fields.source')}: {source}
                        </Text>
                    ) : null}
                    {record.confidenceScore ? (
                        <Text size="xs" c="dimmed">
                            {t('fields.confidence')}: {record.confidenceScore}
                        </Text>
                    ) : null}
                    {verified ? (
                        <Text size="xs" c="dimmed">
                            {t('fields.lastVerified')}: {verified}
                        </Text>
                    ) : null}
                    {!verified && computed ? (
                        <Text size="xs" c="dimmed">
                            {t('fields.computedAt')}: {computed}
                        </Text>
                    ) : null}
                </Group>
            </Accordion.Control>
            <Accordion.Panel>
                <FactValueView recordType={record.recordType} value={displayValue} />
                <Accordion variant="contained" mt="md">
                    <Accordion.Item value="raw">
                        <Accordion.Control>{t('detail.factRawJson')}</Accordion.Control>
                        <Accordion.Panel>
                            {Object.keys(record.recordValue ?? {}).length > 0 ? (
                                <JsonBlock value={record.recordValue} />
                            ) : (
                                <Text size="sm" c="dimmed">
                                    {empty}
                                </Text>
                            )}
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            </Accordion.Panel>
        </Accordion.Item>
    );
}
